<template>
  <div
    class="explorer-controls">

    <div class="label">
      <v-select
        :disabled="false"
        :searchable="false"
        :options="filterOptions"
        form-size="xs"
        :value="selectedFilter"
        v-model="selectedFilter"
        dense/>
      <!--         -->

    </div>

    <div class="medium-wide-size margin-left-l margin-right-s">
      <font-awesome-icon
        class="margin-top-s fa-lg cursor-pointer"
        :icon="icon.doubleLeft"
        v-on:click="updateSelectedDate(-7)"
      />
    </div>
    <div class="medium-wide-size">
      <font-awesome-icon
        class="margin-top-s fa-lg cursor-pointer"
        :icon="icon.left"
        v-on:click="updateSelectedDate(-1)"
      />
    </div>

    <div class="filter">
      <vue-datepicker-local
        class="medium-wide-size margin-left-m"
        v-on:input="$emit('input',selectedDay)"
        :format="dateFormat"
        v-model="selectedDay"
        v-bind="datepickerOptions"
        :disabled-date="(date) => date > new Date()"
        :local="local"/>
    </div>
    <div class="medium-wide-size margin-left-m">
      <font-awesome-icon
        class="margin-top-s fa-lg cursor-pointer"
        :icon="icon.right"
        v-on:click="updateSelectedDate(1)"
      />
    </div>
    <div class="medium-wide-size margin-left-s">
      <font-awesome-icon
        class="margin-top-s fa-lg cursor-pointer"
        :icon="icon.doubleRight"
        v-on:click="updateSelectedDate(7)"
      />
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import VueDatepickerLocal from 'vue-datepicker-local';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import localeData from 'dayjs/plugin/localeData';
import isoWeek from 'dayjs/plugin/isoWeek';
import vSelect from 'vue-select';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faAngleRight,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleDoubleLeft
} from '@fortawesome/fontawesome-free-solid';

export default {
  name: 'DashboardExplorerControls',
  components: {
    vSelect,
    VueDatepickerLocal,
    FontAwesomeIcon
  },
  props: {
    mode: {
      type: String,
      default: 'day'
    },
    dateValue: {
      type: Date,
      default: () => new Date()
    },
    filterValue: {
      type: Object
    },
    filters: {
      type: Array
    },
    listenKeyEvents: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      selectedDay: this.dateValue,
      filterOptions: this.filters,
      selectedFilter: this.filterValue,
      icon: {
        right: faAngleRight,
        doubleRight: faAngleDoubleRight,
        left: faAngleLeft,
        doubleLeft: faAngleDoubleLeft
      }
    };
  },
  watch: {
    selectedFilter (selectedFilter) {
      this.$emit('inputFilter', selectedFilter);
    },
    filterValue (filterValue) {
      this.selectedFilter = filterValue;
    }
  },
  created () {
    dayjs.extend(localizedFormat);
    dayjs.extend(localeData);
    dayjs.localeData();
    dayjs.extend(isoWeek);
    window.addEventListener('keydown', (e) => {
      if (!this.listenKeyEvents) {
        return;
      }
      if (e.key === 'ArrowRight' && (this.selectedDay < new Date().setHours(0, 0, 0, 0))) {
        this.updateSelectedDate(1);
      } else if (e.key === 'ArrowLeft') {
        this.updateSelectedDate(-1);
      }
    });
  },
  computed: {
    ...mapGetters([
      'getLanguageCode'
    ]),
    local () {
      return {
        dow: this.getLanguageCode === 'es' ? 1 : 0,
        hourTip: this.$t('filter.hour'), // tip of select hour
        minuteTip: this.$t('filter.minute'), // tip of select minute
        secondTip: this.$t('filter.second'), // tip of select second
        yearSuffix: '', // suffix of head year
        monthsHead: this.getMonths(), // months of head
        months: this.getMonthsShort(), // months of panel
        weeks: this.getWeekDays(), // weeks
        cancelTip: this.$t('filter.cancel'),
        submitTip: this.$t('filter.submit')
      };
    },
    dateFormat () {
      return this.mode === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD';
    },
    datepickerOptions () {
      return this.mode === 'month' ? { viewMode: 'months', minView: 'months' } : {};
    }
  },
  methods: {
    getMonths () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const months = dayjs.months();
      return months.map(month => this.capitalize(month));
    },
    getMonthsShort () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const months = dayjs.monthsShort();
      return months.map(month => this.removeLastCharacter(this.capitalize(month)));
    },
    getWeekDays () {
      const languageCode = this.getLanguageCode;
      dayjs.locale(languageCode);
      const days = dayjs.weekdaysShort(true);
      return days.map(day => this.removeLastCharacter(this.capitalize(day)));
    },
    capitalize (word) {
      return word.charAt(0).toUpperCase() + word.slice(1);
    },
    removeLastCharacter (str) {
      return str.substring(0, str.length - 1);
    },
    updateSelectedDate (days) {
      const newDate = new Date(this.selectedDay.getTime());

      if (this.mode === 'month') {
        if (Math.abs(days) === 1) {
          newDate.setMonth(this.selectedDay.getMonth() + days);
        } else {
          newDate.setFullYear(this.selectedDay.getFullYear() + (days > 0 ? 1 : -1));
        }
      } else {
        newDate.setDate(this.selectedDay.getDate() + days);
      }

      this.selectedDay = (newDate > new Date()) ? new Date() : newDate;
      this.$emit('input', this.selectedDay);
    }

  }
};
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/variables.scss";

.explorer-controls {
  display: flex;
  align-items: center;
}

.label {
  font-size: 0.875rem;
  margin-right: $sp-margin-smaller;
  @media (max-width: $screen-l-max) {
    display: block;
    margin-bottom: $sp-margin-smaller;
  }
}

.filter {
  @media (max-width: $screen-l-max) {
    width: 100%;
    margin-bottom: $sp;
  }
}

.cursor-pointer{
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/components/explorer/DashboardExplorerControls.vue